import { isEmpty } from "@utils";
import { LOCAL_STORAGE_KEYS, EMPTY_KEY, MAX_TRIES_PER_LEVEL } from "@consts";

export const state = () => ({
	currentLevelIndex: 0,
	currentLevelLength: 0,
	currentLevelSolution: null,
	lastLevelIndex: 0,
	lastLevelLength: 0,
	animateTilesCorrect: false,
	animateTilesWrong: false,
});

export const mutations = {
	setState(state, payload) {
		if (payload.lastLevelIndex >= 0) {
			state.currentLevelIndex = payload.lastLevelIndex + 1;
			state.lastLevelIndex = payload.lastLevelIndex;
		}

		if (payload.lastLevelLength >= 0) {
			state.currentLevelLength = payload.lastLevelLength - 1;
			state.lastLevelLength = payload.lastLevelLength;
		}

		if (!isEmpty(payload.levelSolution)) {
			state.currentLevelSolution = payload.levelSolution;
		}
	},
	updateAnimateTilesCorrect(state, payload) {
		state.animateTilesCorrect = payload;
	},
	updateAnimateTilesWrong(state, payload) {
		state.animateTilesWrong = payload;
	},
};

export const getters = {
	getAllWordsOfLevelFromPyramid(state, getters, rootState) {
		return rootState.pyramid.pyramidOfWords[state.currentLevelIndex];
	},
	getMaxTriesForLevel() {
		// currentLevelIndex is 1-based for guessable levels: pyramidOfWords[0] is the
		// given starting word (never guessed), so the first real level is index 1.
		// MAX_TRIES_PER_LEVEL is 0-based, so shift the lookup back by one.
		return (levelIndex) =>
			MAX_TRIES_PER_LEVEL[levelIndex - 1] ??
			MAX_TRIES_PER_LEVEL[MAX_TRIES_PER_LEVEL.length - 1];
	},
	// The level index the tries counter should display. Normally this is just
	// currentLevelIndex, but once the game is over CHANGE_LEVEL has already
	// bumped currentLevelIndex one past the last guessable level (that's how
	// FINISH_GAME is triggered), so the counter would point at a phantom
	// level with zero recorded tries and render every triangle as still
	// available (WFW-6683). Clamp it to the last real level instead.
	getDisplayLevelIndex(state, getters, rootState) {
		const lastGuessableLevelIndex =
			rootState.pyramid.pyramidOfWords.length - 1;

		if (lastGuessableLevelIndex < 1) {
			return state.currentLevelIndex;
		}

		return Math.min(state.currentLevelIndex, lastGuessableLevelIndex);
	},
	getTriesUsedForCurrentLevel(state, getters, rootState) {
		return rootState.input.triedOutWords.filter(
			(entry) =>
				entry.level === getters.getDisplayLevelIndex &&
				entry.tilesMap.includes("w")
		).length;
	},
	getMaxTriesForCurrentLevel(state, getters) {
		return getters.getMaxTriesForLevel(getters.getDisplayLevelIndex);
	},
	getTriesRemainingForCurrentLevel(state, getters) {
		return Math.max(
			getters.getMaxTriesForCurrentLevel -
				getters.getTriesUsedForCurrentLevel,
			0
		);
	},
};

export const actions = {
	/*
		This action takes the user input and compares it to the found solution.
		Marks the correct tiles inside the input and the keyboard.
		If all tiles are correct then dispatches to the next level.
		If not, then it removes the incorrect tiles from the input while removing the correct tiles from the keyboard.
	 */
	CHECK_SOLUTION({
		commit,
		state,
		dispatch,
		getters,
		rootState,
		rootGetters,
	}) {
		if (state.currentLevelSolution) {
			rootState.input.tiles[rootState.level.currentLevelIndex].forEach(
				(tile, tileIndex) => {
					const tileValue = tile.keyValue;
					const solutionValue =
						state.currentLevelSolution.word[tileIndex];

					if (tileValue === solutionValue) {
						commit(
							"input/updateTile",
							{
								levelIndex: rootState.level.currentLevelIndex,
								tileInfo: {
									...tile,
									keyFit: true,
									keyPressed: true,
								},
							},
							{ root: true }
						);

						commit(
							"input/updateTile",
							{
								levelIndex: rootState.level.lastLevelIndex,
								tileInfo: {
									...tile,
									keyFit: true,
									keyPressed: true,
								},
							},
							{ root: true }
						);
					}
				}
			);
		}

		// The word to save in the list of last tried out words
		const word = rootState.input.tiles[rootState.level.currentLevelIndex]
			.map((element) => element.keyValue)
			.join("");

		// String representation of the correct and wrong tiles inside the input
		const tilesMap = rootState.input.tiles[
			rootState.level.currentLevelIndex
		].reduce((accumulator, element) => {
			return `${accumulator}${element.keyFit ? "c" : "w"}`;
		}, "");

		// If clues are currently visible, the player has seen this level's clue.
		// This sticks for the rest of the day, even if clues get switched off later.
		if (rootState.pyramid.showClues) {
			commit("pyramid/markClueSeenForLevel", state.currentLevelIndex, {
				root: true,
			});
		}

		const mode = rootState.pyramid.cluesSeenByLevel[state.currentLevelIndex]
			? "easy"
			: "hard";

		const maxTriesForLevel = getters.getMaxTriesForLevel(
			state.currentLevelIndex
		);

		const wrongTriesBeforeThisAttempt =
			rootState.input.triedOutWords.filter(
				(entry) =>
					entry.level === state.currentLevelIndex &&
					entry.tilesMap.includes("w")
			).length;

		const isThisAttemptWrong = tilesMap.includes("w");

		const triesUsedForLevel =
			wrongTriesBeforeThisAttempt + (isThisAttemptWrong ? 1 : 0);

		const triesLeft = Math.max(maxTriesForLevel - triesUsedForLevel, 0);

		commit(
			"input/addToTriedOutWords",
			{
				word,
				tilesMap,
				level: state.currentLevelIndex,
				mode,
				triesLeft,
			},
			{ root: true }
		);

		// If all the tiles are correct, then go to next level
		if (
			rootState.input.tiles[rootState.level.currentLevelIndex].every(
				(element) => element.keyFit === true
			)
		) {
			dispatch("CHANGE_LEVEL");
		} else if (triesUsedForLevel >= maxTriesForLevel) {
			// Out of tries: reveal the solution and bump the player to the next level
			dispatch("REVEAL_AND_ADVANCE");
		} else {
			// Handle the incorrect tiles
			rootState.input.tiles[rootState.level.currentLevelIndex]
				.filter((element) => element.keyFit === false)
				.forEach((tile, tileIndex) => {
					commit("updateAnimateTilesWrong", true);

					// Remove the incorrect tiles from the input
					commit(
						"input/removeTile",
						{
							levelIndex: state.currentLevelIndex,
							tileInfo: tile,
						},
						{ root: true }
					);

					// Re enable the incorrect keys in the keyboard
					commit(
						"input/updateTile",
						{
							levelIndex: state.lastLevelIndex,
							tileInfo: { ...tile, keyPressed: false },
						},
						{ root: true }
					);
				});
		}

		// Save to local storage (this action only ever runs client-side, in response
		// to a keypress, but the guard is here too in case that ever changes).
		// Skipped for past puzzles (/play/pirmyd/:date/) — only today's progress
		// is meant to be restored on reload.
		if (process.client && !rootGetters["navigation/isFormerDatePirmyd"]) {
			localStorage.setItem(
				LOCAL_STORAGE_KEYS.PRMD_TRIED_WORDS,
				JSON.stringify(rootState.input.triedOutWords)
			);
			localStorage.setItem(
				LOCAL_STORAGE_KEYS.PRMD_CURRENT_LEVEL_INDEX,
				rootState.level.currentLevelIndex
			);
			localStorage.setItem(
				LOCAL_STORAGE_KEYS.PRMD_CURRENT_LEVEL_LENGTH,
				rootState.level.currentLevelLength
			);
			localStorage.setItem(
				LOCAL_STORAGE_KEYS.PRMD_LAST_LEVEL_INDEX,
				rootState.level.lastLevelIndex
			);
			localStorage.setItem(
				LOCAL_STORAGE_KEYS.PRMD_LAST_LEVEL_LENGTH,
				rootState.level.lastLevelLength
			);
			localStorage.setItem(
				LOCAL_STORAGE_KEYS.PRMD_TILES,
				JSON.stringify(rootState.input.tiles)
			);
			localStorage.setItem(
				LOCAL_STORAGE_KEYS.PRMD_DAY,
				`${new Date().getDate()}`
			);
			localStorage.setItem(
				LOCAL_STORAGE_KEYS.PRMD_MONTH,
				`${new Date().getMonth() + 1}`
			);
			localStorage.setItem(
				LOCAL_STORAGE_KEYS.PRMD_SHOW_CLUES,
				JSON.stringify(rootState.pyramid.showClues)
			);
			localStorage.setItem(
				LOCAL_STORAGE_KEYS.PRMD_CLUES_SEEN_BY_LEVEL,
				JSON.stringify(rootState.pyramid.cluesSeenByLevel)
			);
		}
	},
	/*
		Called when the player has used up all their tries for the current level.
		Fills in the current level's tiles with the actual solution, marks them as
		correct, and then bumps the player to the next level.
	 */
	REVEAL_AND_ADVANCE({ commit, state, dispatch, rootState }) {
		if (!state.currentLevelSolution) {
			return;
		}

		// Clear the "selected" (pressed) state that the final wrong guess left
		// on the source row. The normal wrong-guess path in CHECK_SOLUTION does
		// this when it removes the wrong tiles from the input, but this path
		// replaces the input with the solution outright, so a wrongly guessed
		// source tile (keyPressed: true, keyFit: false) would otherwise keep its
		// blue "selected" outline for the rest of the game (WFW-6683).
		rootState.input.tiles[state.lastLevelIndex]
			.filter((tile) => tile.keyPressed && !tile.keyFit)
			.forEach((tile) => {
				commit(
					"input/updateTile",
					{
						levelIndex: state.lastLevelIndex,
						tileInfo: { ...tile, keyPressed: false },
					},
					{ root: true }
				);
			});

		const revealedTiles = state.currentLevelSolution.word
			.split("")
			.map((letter, letterIndex) => ({
				...EMPTY_KEY,
				keyValue: letter,
				keyId: letterIndex,
				keyFit: true,
				keyPressed: true,
			}));

		commit(
			"input/setLevelTiles",
			{ levelIndex: state.currentLevelIndex, tiles: revealedTiles },
			{ root: true }
		);

		dispatch("CHANGE_LEVEL");
	},
	CHANGE_LEVEL({ commit, state, dispatch, getters, rootState }) {
		// Set the state for the new level
		const lastLevelIndex = state.currentLevelIndex;
		const lastLevelLength = state.currentLevelLength;
		const levelSolution =
			rootState.pyramid.pyramidOfWords[state.currentLevelIndex + 1];
		commit("setState", { lastLevelIndex, lastLevelLength, levelSolution });

		// If the current level is the last one it means the game is over —
		// either the player guessed it or ran out of tries on it. Which one
		// doesn't matter here: it's derived separately, per level, from
		// input/triedOutWords (see common/utils/pirmyd/share.js) wherever the
		// UI needs to know how many levels the player actually solved.
		if (
			state.currentLevelIndex === rootState.pyramid.pyramidOfWords.length
		) {
			dispatch("FINISH_GAME");
			return;
		}

		commit("updateAnimateTilesCorrect", true);

		// reset all tiles as incorrect. if not, they will be disabled
		commit("input/updatedAllTilesToNotCorrect", {}, { root: true });
	},
	FINISH_GAME({ commit, dispatch }) {
		// Signal the game is over in the vue store.
		commit("pyramid/updatePyramidSolved", true, { root: true });

		// Pulse: lets pages/Pirmyd/Pirmyd.js tell this "just finished, live"
		// transition apart from a puzzle restored as already-finished from
		// local storage on page load, so it only delays opening the
		// ResultDialog in the former case.
		commit("pyramid/updateJustFinishedLive", true, { root: true });

		// Save the outcome in the store and local storage (today's puzzle
		// only — see SET_STATS)
		dispatch("stats/SET_STATS", null, { root: true });

		// Log this date as played (today's or a past puzzle, won or lost) so
		// "Random Day" in the date picker never re-serves it
		dispatch("stats/RECORD_SOLVED_DATE", null, { root: true });
	},
};
